import {
  getUrlParam,
  isInWxFromUrlQuery,
  normalizeAuthToken,
  safeLocalStorageGet,
  safeLocalStorageSet,
} from './common'
import { isApp, isHanziApp, isSduckApp, isSnoApp } from './EnvUtils'
import { JsBridge, messageMethodIsSupport } from './JsBridge'

export const DEFAULT_SNO_APP_ID = 'l0qyjv8uf0fil6xm'
export const DEFAULT_SDUCK_APP_ID = 'rz6svwbgy01o26xb'
/** 天才鸭汉字独立 App（与 Flutter `BizAppId.hanzi` 一致） */
export const DEFAULT_HANZI_APP_ID = 'SWYUxK5mLlqnpaTt'
/** 题霸优学 / 小四门小程序 */
export const DEFAULT_XSM_APP_ID = 'uro25b6xwubbvfpi'

/** 仅保存 JsBridge `setUserInfo` 写入的 token，与 URL token 分离 */
const BRIDGE_TOKEN_STORAGE_KEY = 'ticket_token_bridge'

export type WebviewAuthInitOptions = {
  /** URL 未带 appId 时写入 localStorage 的默认值 */
  defaultAppId?: string
  /** 为 true 时按 sno / 天才鸭 App 环境自动选择默认 appId（与 defaultAppId 同时存在时后者优先） */
  resolveDefaultAppIdFromEnv?: boolean
}

let bridgeAuthTokenMem = ''

function readAppIdFromUserAgent(): string {
  const ua =
    typeof navigator !== 'undefined' && typeof navigator.userAgent === 'string'
      ? navigator.userAgent
      : ''
  const match = ua.match(/X-App-Id_([^\s]+)/)
  return (match?.[1] || '').trim()
}

function readTokenFromUserAgent(): string {
  const ua =
    typeof navigator !== 'undefined' && typeof navigator.userAgent === 'string'
      ? navigator.userAgent
      : ''
  const match = ua.match(/Authorization_Bearer\s([^\s]+)/)
  return normalizeAuthToken(match?.[1])
}

/** 桥接 token：内存（setUserInfo 当次会话）→ ticket_token_bridge */
function readBridgeAuthToken(): string {
  const fromMem = normalizeAuthToken(bridgeAuthTokenMem)
  if (fromMem) return fromMem
  const fromStorage = normalizeAuthToken(
    safeLocalStorageGet(BRIDGE_TOKEN_STORAGE_KEY)
  )
  if (fromStorage) {
    bridgeAuthTokenMem = fromStorage
    return fromStorage
  }
  return ''
}

/** local 兜底：ticket_token（URL 写入 / 历史兼容） */
function readLocalAuthToken(): string {
  return normalizeAuthToken(safeLocalStorageGet('ticket_token'))
}

function syncFreshTokenToLocalStorage(token: string): void {
  const normalized = normalizeAuthToken(token)
  if (normalized) {
    safeLocalStorageSet('ticket_token', normalized)
  }
}

/** 解析业务 appId：URL → localStorage → App UA → 宿主环境默认 */
export function getWebviewAuthAppId(): string {
  const fromQuery = getUrlParam('appId').trim()
  if (fromQuery) return fromQuery
  const fromStorage = safeLocalStorageGet('ticket_appId')?.trim()
  if (fromStorage) return fromStorage
  const fromUa = readAppIdFromUserAgent()
  if (fromUa) return fromUa
  if (getUrlParam('vipType') === 'hanzi') return DEFAULT_HANZI_APP_ID
  if (isInWxFromUrlQuery()) return DEFAULT_XSM_APP_ID
  if (isHanziApp()) return DEFAULT_HANZI_APP_ID
  if (isSnoApp()) return DEFAULT_SNO_APP_ID
  if (isSduckApp()) return DEFAULT_SDUCK_APP_ID
  return DEFAULT_SDUCK_APP_ID
}

/**
 * 从 App `setUserInfo` 桥接消息解析 token。
 * 临时兼容历史字段拼写 `toke`，下一版发版后仅保留 `token`。
 */
export function resolveSetUserInfoToken(
  data: { token?: unknown; toke?: unknown } | null | undefined
): string {
  if (!data) return ''
  const raw = data.token ?? data.toke ?? ''
  return normalizeAuthToken(String(raw))
}

/** 将桥接鉴权 token 写入内存与专用 storage（不混用 URL token） */
export function persistBridgeAuthToken(token: string): void {
  const normalized = normalizeAuthToken(token)
  if (!normalized) return
  bridgeAuthTokenMem = normalized
  safeLocalStorageSet(BRIDGE_TOKEN_STORAGE_KEY, normalized)
  // 兼容仍直接读 ticket_token 的旧页面
  safeLocalStorageSet('ticket_token', normalized)
}

/**
 * 鉴权 token 优先级：
 * App UA → URL query `token` → 桥接 setUserInfo → localStorage ticket_token。
 * UA 命中时回写 ticket_token，避免后续请求读到过期 local 值。
 */
export function getWebviewAuthToken(): string {
  const fromUa = readTokenFromUserAgent()
  if (fromUa) {
    syncFreshTokenToLocalStorage(fromUa)
    return fromUa
  }
  const fromQuery = normalizeAuthToken(getUrlParam('token'))
  if (fromQuery) return fromQuery
  const fromBridge = readBridgeAuthToken()
  if (fromBridge) return fromBridge
  return readLocalAuthToken()
}

/**
 * 从 URL / App UA 同步 appId 到 localStorage。
 * URL 带 token 时写入 local 作跨 WebView 兜底（读取时仍低于 UA / URL）。
 */
export function initWebviewAuthFromUrl(options?: WebviewAuthInitOptions): void {
  const tokenFromUrl = normalizeAuthToken(getUrlParam('token'))
  if (tokenFromUrl) {
    safeLocalStorageSet('ticket_token', tokenFromUrl)
  }

  let appId = getUrlParam('appId').trim()
  if (!appId) {
    appId = readAppIdFromUserAgent()
  }
  if (!appId) {
    if (options?.defaultAppId) {
      appId = options.defaultAppId
    } else if (options?.resolveDefaultAppIdFromEnv) {
      if (isHanziApp()) {
        appId = DEFAULT_HANZI_APP_ID
      } else if (isSnoApp()) {
        appId = DEFAULT_SNO_APP_ID
      } else if (isSduckApp()) {
        appId = DEFAULT_SDUCK_APP_ID
      }
    }
  }
  if (!appId && isInWxFromUrlQuery()) {
    appId = DEFAULT_XSM_APP_ID
  }
  if (appId) {
    safeLocalStorageSet('ticket_appId', appId)
  }
}

/** 是否已有可发起接口请求的登录凭证 */
export function hasWebviewAuth(): boolean {
  return Boolean(getWebviewAuthToken())
}

/**
 * App 内嵌页：等待鉴权 token 就绪后再调需登录接口。
 * UA / URL / 桥接 / local 已有则立即返回；否则触发 getUserInfo 并短时等待 setUserInfo。
 */
export function waitForWebviewAuthToken(timeoutMs = 3000): Promise<string> {
  const existing = getWebviewAuthToken()
  if (existing) {
    return Promise.resolve(existing)
  }
  if (typeof window === 'undefined' || !isApp() || !messageMethodIsSupport('getUserInfo')) {
    return Promise.resolve('')
  }

  return new Promise((resolve) => {
    let settled = false
    const finish = () => {
      if (settled) return
      settled = true
      window.removeEventListener('message', onMessage)
      clearTimeout(timer)
      resolve(getWebviewAuthToken())
    }

    const onMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'setUserInfo') return
      const token = resolveSetUserInfoToken(event.data)
      persistBridgeAuthToken(token)
      finish()
    }

    const timer = window.setTimeout(finish, timeoutMs)
    window.addEventListener('message', onMessage)
    JsBridge.getUserInfo({ sourceFrom: 'h5' })
  })
}
